import { useEffect, useState } from 'react';
import { userService } from '../service/instances';
import {
  errorToast,
  isApiError,
  validateAndDecodeAuthToken,
} from '../utils/helper';
import {
  AuthorizedUsersResponse,
  AuthTokenResponse,
  LoginAsStaffResponseData,
  UsersResponse,
} from '../service/service.type';
import {
  CreateUserResponse,
  GetUsersResponse,
  UpdateUserResponse,
  UserStatus,
} from '@inplay/models';
import {
  apiStatusCode,
  CreateUserStatusResponse,
  StorageKey,
} from '../utils/helper.type';
import { useUserSummary } from '../service/context/getUserContext';
import { isUndefined } from 'lodash';
import { useAuth } from '../service/context/authContext';
import { PinOptionType } from '../component/userModal/type';
import { AddOrganizationUnitResponse } from '../pages/userManagement/venuesView/venuesView.type';

export const useUsersSummary = (
  autoFetch = false,
): {
  usersSummary: UsersResponse | undefined;
  userSummaryLoading: boolean;
  updateUserLocally: (userId: string | string[], isActive: boolean) => void;
  deleteUserLocally: (userId: string | string[]) => void;
  fetchUsersData: () => Promise<void>;
} => {
  const [userSummaryLoading, setUserSummaryLoading] = useState<boolean>(true);
  const { usersSummary, setUsersSummary } = useUserSummary();
  const fetchUsersData = async () => {
    console.log(
      `[useUsersSummary][fetchUsersData] Before calling fetchUsersData...`,
    );
    setUserSummaryLoading(true);

    try {
      const data: UsersResponse = await userService.getUserSummary();

      console.log(
        `[useUsersSummary][fetchUsersData] After calling fetchUsersData`,
        { data },
      );

      setUsersSummary(data);
    } catch (error: unknown) {
      errorToast(error);
      console.error(`[useUsersSummary][fetchUsersData] Error:`, error);
    } finally {
      console.log(`[useUsersSummary][fetchUsersData] finally block`);
      setUserSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchUsersData();
    }
  }, [autoFetch]);

  //TODO: Remove this function once the UI to show the asynchronous update operation is implemented.
  const updateUserLocally = (userId: string | string[], isActive: boolean) => {
    const idsToUpdate: string[] = Array.isArray(userId) ? userId : [userId];

    setUsersSummary((prevUser: UsersResponse | undefined) => {
      if (!prevUser) return prevUser;

      return {
        ...prevUser,
        data: prevUser.data.map((user: GetUsersResponse) =>
          idsToUpdate.includes(user.id)
            ? {
                ...user,
                status: isActive ? UserStatus.ACTIVE : UserStatus.INACTIVE,
              }
            : user,
        ),
      };
    });
  };

  //TODO: Remove this function once the UI to show the asynchronous delete operation is implemented.
  const deleteUserLocally = (userId: string | string[]) => {
    const idsToDelete: string[] = Array.isArray(userId) ? userId : [userId];

    setUsersSummary((prevUser: UsersResponse | undefined) => {
      if (!prevUser) return prevUser;

      return {
        ...prevUser,

        data: prevUser.data.filter(
          (user: GetUsersResponse) => !idsToDelete.includes(user.id),
        ),
      };
    });
  };

  return {
    usersSummary,
    userSummaryLoading,
    updateUserLocally,
    deleteUserLocally,
    fetchUsersData,
  };
};

export const useResendInvitationEmail = (): {
  invitationEmail: (email: string) => Promise<void>;
  invitationEmailLoading: boolean;
} => {
  const [invitationEmailLoading, setInvitationEmailLoading] =
    useState<boolean>(false);

  const invitationEmail = async (email: string) => {
    if (!email) return;
    console.log(`[useResendInvitation] Before calling invitation...`);
    setInvitationEmailLoading(true);
    try {
      await userService.resendInvitationEmail(email);
    } catch (error: unknown) {
      console.error(`[useResendInvitation][ERROR]`, { error });
      errorToast(error);
    } finally {
      console.error(`[useResendInvitation] finally block`);
      setInvitationEmailLoading(false);
    }
  };

  return { invitationEmail, invitationEmailLoading };
};

export const useActivateDeactivate = (): {
  activateDeactivateUser: (
    ids: string | string[],
    setToActive: boolean,
  ) => Promise<UpdateUserResponse | undefined>;
  activateDeactivateLoading: boolean;
} => {
  const [activateDeactivateLoading, setActivateDeactivateLoading] =
    useState<boolean>(false);

  const activateDeactivateUser = async (
    ids: string | string[],
    setToActive: boolean,
  ) => {
    if (!ids && !setToActive) return;
    console.log(
      `[useActivateDeactivate] Before calling activate deactivate...`,
    );
    setActivateDeactivateLoading(true);
    try {
      const userActiveDeActiveResponse: UpdateUserResponse =
        await userService.activeDeactivateUser(ids, setToActive);
      return userActiveDeActiveResponse;
    } catch (error: unknown) {
      console.error(`[useActivateDeactivate][ERROR]`, { error });
      errorToast(error);
      console.error('[useActivateDeactivate] Error fetching data:', error);
    } finally {
      console.log(`[useActivateDeactivate] finally block`);
      setActivateDeactivateLoading(false);
    }
  };

  return { activateDeactivateUser, activateDeactivateLoading };
};

export const useDeleteUser = (): {
  deleteUser: (
    userIds: string | string[],
  ) => Promise<UpdateUserResponse | undefined>;
  deleteUserLoading: boolean;
} => {
  const [deleteUserLoading, setDeleteUserLoading] = useState<boolean>(false);

  const deleteUser = async (userIds: string | string[]) => {
    if (!userIds) return;
    console.log(
      `[useActivateDeactivate] Before calling activate deactivate...`,
    );
    setDeleteUserLoading(true);
    try {
      const userDeleteResponse: UpdateUserResponse =
        await userService.deleteUser(userIds);
      return userDeleteResponse;
    } catch (error: unknown) {
      console.error(`[useDeleteUser][ERROR]`, { error });
      errorToast(error);
      console.error('[useDeleteUser] Error fetching data:', error);
    } finally {
      console.log(`[useDeleteUser] finally block`);
      setDeleteUserLoading(false);
    }
  };

  return { deleteUser, deleteUserLoading };
};

export const useEditVenue = (): {
  editVenue: (
    organizationUnitId: string,
    name: string,
  ) => Promise<UpdateUserResponse>;
  apiError: string | null;
  editVenueLoading: boolean;
} => {
  const [editVenueLoading, setEditVenueLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const editVenue = async (
    organizationUnitId: string,
    name: string,
  ): Promise<UpdateUserResponse> => {
    if (!organizationUnitId || !name) {
      /* empty */
    }

    console.log(`[useEditVenue] Before calling edit venue...`);
    setEditVenueLoading(true);
    setApiError(null);
    try {
      const userEditVenueResponse: UpdateUserResponse =
        await userService.editVenue(organizationUnitId, name);
      return userEditVenueResponse;
    } catch (error: unknown) {
      console.error(`[useEditVenue][ERROR]`, { error });
      if (isApiError(error)) {
        setApiError(error.response?.data?.message);
      } else {
        setApiError('An unexpected error occurred');
      }
      console.error('[useActivateDeactivate] Error fetching data:', error);
      throw error;
    } finally {
      console.log(`[useActivateDeactivate] finally block`);
      setEditVenueLoading(false);
    }
  };

  return { editVenue, editVenueLoading, apiError };
};

export const useLoginAsStaff = (): {
  loginAsStaff: (
    pin: string,
    venueId?: string,
  ) => Promise<LoginAsStaffResponseData | undefined>;
  loginAsStaffLoading: boolean;
  loginAsStaffError: string | null;
} => {
  const [loginAsStaffLoading, setLoginAsStaffLoading] =
    useState<boolean>(false);
  const [loginAsStaffError, setLoginAsStaffError] = useState<string | null>(
    null,
  );
  const { setAccessToken, setStaffPin } = useAuth();

  const loginAsStaff = async (
    pin: string,
    venueId?: string,
  ): Promise<LoginAsStaffResponseData | undefined> => {
    console.log(`[useLoginAsStaff] Before calling login as staff...`);
    setLoginAsStaffLoading(true);
    setLoginAsStaffError(null);
    try {
      const loginAsStaffResponse: LoginAsStaffResponseData =
        await userService.loginAsStaff(pin, venueId);
      if (loginAsStaffResponse.statusCode === apiStatusCode.SUCCESS) {
        if (loginAsStaffResponse.data) {
          setStaffPin(loginAsStaffResponse.hashedPin);
          localStorage.setItem(
            StorageKey.EXPIRY_TIME,
            String(loginAsStaffResponse.data.expiryTime),
          );
          const staffAccessToken = localStorage.getItem(
            StorageKey.STAFF_ACCESS_TOKEN,
          );
          const staffRefreshToken = localStorage.getItem(
            StorageKey.STAFF_REFRESH_TOKEN,
          );
          setAccessToken(staffAccessToken);
          localStorage.setItem(
            StorageKey.REFRESH_TOKEN,
            staffRefreshToken || '',
          );
        }
      }
      return loginAsStaffResponse;
    } catch (error: unknown) {
      console.error(`[useLoginAsStaff][ERROR]`, { error });
      if (isApiError(error)) {
        if (error.response?.data.statusCode === 404) {
          setLoginAsStaffError(
            'No user found with this PIN. Please try again.',
          );
        } else
          setLoginAsStaffError(
            error.response?.data?.message || 'An error occurred',
          );
      } else {
        setLoginAsStaffError('An unexpected error occurred');
      }
    } finally {
      setLoginAsStaffLoading(false);
      console.log(`[useLoginAsStaff] finally block`);
    }
  };
  return { loginAsStaff, loginAsStaffLoading, loginAsStaffError };
};

export const useCreateUser = (): {
  createUser: (params: {
    firstName: string;
    lastName: string;
    email?: string;
    profilePhoto?: string;
    organizationId?: string;
    subOrganizationId?: string;
    subOrganizationIds?: string[];
    venueId?: string;
    venueBranchIds?: string[];
    venueBranchId?: string;
    mobileCountryCode?: string;
    mobileNumber?: string;
    pin?: string;
    pinOption?: string;
    type?: string;
    fetchUsersData?: () => Promise<void>;
  }) => Promise<CreateUserResponse | null>;
  createUserLoading: boolean;
} => {
  const [createUserLoading, setCreateUserLoading] = useState<boolean>(false);

  const createUser = async (
    params: any, //TODO add type later for Params
  ): Promise<CreateUserResponse | null> => {
    const {
      firstName,
      lastName,
      email,
      profilePhoto,
      organizationId,
      subOrganizationId,
      subOrganizationIds,
      venueId,
      venueBranchIds,
      venueBranchId,
      mobileCountryCode,
      mobileNumber,
      pin,
      pinOption,
      type,
      fetchUsersData,
    } = params;

    if (!firstName && lastName) return null;

    setCreateUserLoading(true);
    try {
      console.log(`[Update User] Before calling update api...`);
      const createUserKeyResponse: CreateUserResponse =
        await userService.createUser(
          firstName,
          lastName,
          email,
          profilePhoto,
          organizationId,
          subOrganizationId,
          subOrganizationIds,
          venueId,
          venueBranchIds,
          venueBranchId,
          mobileCountryCode,
          mobileNumber,
          pin,
          pinOption === PinOptionType.DEFAULT
            ? PinOptionType.DEFAULT
            : PinOptionType.CUSTOM,
          type,
          fetchUsersData,
        );
      console.log(`[useCreateUser][RESPONSE]`, createUserKeyResponse);
      if (!isUndefined(createUserKeyResponse)) {
        const createUserResponse: CreateUserStatusResponse | null =
          createUserKeyResponse && {
            statusCode: apiStatusCode.SUCCESS,
            data: createUserKeyResponse,
          };
        console.log(`[CreateUser] response `, createUserResponse);
        if (createUserResponse?.statusCode === apiStatusCode.SUCCESS) {
          console.log(`[CreateUser] close modal `);

          await fetchUsersData();
        }
      }

      return createUserKeyResponse;
    } catch (error: unknown) {
      console.error(`[useCreateUser][ERROR]`, { error });
      errorToast(error);
      return null;
    } finally {
      setCreateUserLoading(false);
    }
  };

  return { createUser, createUserLoading };
};

export const useUpdateUser = (): {
  updateUser: (params: {
    userId: string;
    firstName: string;
    lastName: string;
    email?: string;
    profilePhoto?: string;
    organizationId?: string;
    subOrganizationId?: string;
    subOrganizationIds?: string[];
    venueId?: string;
    venueBranchIds?: string[];
    venueBranchId?: string;
    mobileCountryCode?: string;
    mobileNumber?: string;
    pin?: string;
    type?: string;
    fetchUsersData?: () => Promise<void>;
  }) => Promise<CreateUserResponse | null>;
  updateUserLoading: boolean;
} => {
  const [updateUserLoading, setUpdateUserLoading] = useState(false);

  const updateUser = async (
    params: any, //TODO add type later for Params
  ): Promise<CreateUserResponse | null> => {
    const {
      userId,
      firstName,
      lastName,
      email,
      profilePhoto,
      organizationId,
      subOrganizationId,
      subOrganizationIds,
      venueId,
      venueBranchIds,
      venueBranchId,
      mobileCountryCode,
      mobileNumber,
      pin,
      type,
      fetchUsersData,
    } = params;

    if (!firstName && lastName) return null;

    setUpdateUserLoading(true);
    try {
      console.log(`[useUpdateUser] Before calling update api...`);
      const updateUserResponse: any = await userService.updateUser(
        userId,
        firstName,
        lastName,
        email,
        profilePhoto,
        organizationId,
        subOrganizationId,
        subOrganizationIds,
        venueId,
        venueBranchIds,
        venueBranchId,
        mobileCountryCode,
        mobileNumber,
        pin,
        type,
      );

      if (updateUserResponse?.statusCode === apiStatusCode.SUCCESS) {
        await fetchUsersData();
      }
      console.error(`[useUpdateUser][RESPONSE]`, updateUserResponse);

      return updateUserResponse;
    } catch (error: unknown) {
      console.error(`[useUpdateUser][ERROR]`, { error });
      errorToast(error);
      return null;
    } finally {
      setUpdateUserLoading(false);
    }
  };

  return { updateUser, updateUserLoading };
};

export type UpdateProfileParams = {
  firstName?: string;
  lastName?: string;
  email?: string;
  signature?: string;
  mobileNumber?: number;
  profilePhoto?: string;
};

export const useUpdateProfile = (): {
  updateProfile: (
    params: UpdateProfileParams,
  ) => Promise<UpdateUserResponse | undefined>;
  apiError: string | null;
  updateProfileLoading: boolean;
} => {
  const [updateProfileLoading, setUpdateProfileLoading] =
    useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const updateProfile = async ({
    firstName,
    lastName,
    email,
    signature,
    mobileNumber,
    profilePhoto,
  }: UpdateProfileParams) => {
    console.log(`[useUpdateProfile] Before calling update profile...`);
    setUpdateProfileLoading(true);
    setApiError(null);

    try {
      const response: UpdateUserResponse = await userService.updateProfile(
        firstName,
        lastName,
        email,
        signature,
        mobileNumber,
        profilePhoto,
      );

      return response;
    } catch (error: unknown) {
      console.error(`[useUpdateProfile][ERROR]`, { error });
      if (isApiError(error)) {
        setApiError(error.response?.data?.message ?? 'API error');
      } else {
        setApiError('An unexpected error occurred');
      }
    } finally {
      console.log(`[useUpdateProfile] finally block`);
      setUpdateProfileLoading(false);
    }
  };

  return { updateProfile, updateProfileLoading, apiError };
};

export type AddOrganizationParams = {
  name: string;
  type: string;
  organizationId?: string;
  subOrganizationIds?: string[];
  venueId?: string;
  venueBranchName?: string;
  numberOfGamblingMachines?: string;
  cameraIds?: string[];
};

export const useAddOrganizationUnit = (): {
  addOrgUnit: (
    payload: AddOrganizationParams,
  ) => Promise<AddOrganizationUnitResponse | undefined>;
  apiError: string | null;
  addOrgUnitLoading: boolean;
} => {
  const [addOrgUnitLoading, setAddOrgUnitLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const addOrgUnit = async (payload: AddOrganizationParams) => {
    if (!payload.name || !payload.type) return;

    console.log(`[useAddOrganizationUnit] Before calling add organization...`, {
      payload,
    });
    setAddOrgUnitLoading(true);
    setApiError(null);
    try {
      const userAddOrganizationResponse: AddOrganizationUnitResponse =
        await userService.addOrganizationUnit(payload);
      return userAddOrganizationResponse;
    } catch (error: unknown) {
      console.error(`[useAddOrganizationUnit][ERROR]`, { error });
      if (isApiError(error)) {
        setApiError(error.response?.data?.message);
      } else {
        setApiError('An unexpected error occurred');
      }
      console.error('[useActivateDeactivate] Error fetching data:', error);
      throw error;
    } finally {
      console.log(`[useActivateDeactivate] finally block`);
      setAddOrgUnitLoading(false);
    }
  };

  return { addOrgUnit, addOrgUnitLoading, apiError };
};

/**
 * A hook to fetch authorized users for a given organization.
 * @param organizationUnitId - The organization unit id for which to fetch authorized users.
 * @returns An object containing the following properties:
 *   - authUsersSummary: An array of GetUsersResponse objects which contain the authorized users.
 *   - authUsersLoading: A boolean indicating whether the authorized users are being fetched.
 *   - fetchAuthorizedUsersData: A function to fetch the authorized users data.
 */
export const useAuthUsersSummary = (
  organizationUnitId: string,
  scope: string,
): {
  authUsersSummary: GetUsersResponse[] | undefined;
  authUsersLoading: boolean;
  refreshData: () => void;
} => {
  const [authUsersLoading, setAuthUsersLoading] = useState<boolean>(false);
  const [authUsersSummary, setAuthUsersSummary] = useState<
    GetUsersResponse[] | undefined
  >();
  const [authUsersAPIResponse, setAuthUsersAPIResponse] =
    useState<AuthorizedUsersResponse>();
  const { usersSummary, userSummaryLoading, fetchUsersData } =
    useUsersSummary(true);
  const validAuthToken: AuthTokenResponse | null = validateAndDecodeAuthToken();

  useEffect(() => {
    if (organizationUnitId) {
      fetchAuthorizedUsersData();
    }
  }, [organizationUnitId]);

  useEffect(() => {
    if (authUsersAPIResponse && usersSummary && !userSummaryLoading) {
      filterAuthorizedUsers();
    }
  }, [authUsersAPIResponse, usersSummary, userSummaryLoading]);

  const refreshData = () => {
    if (organizationUnitId) {
      fetchUsersData();
      fetchAuthorizedUsersData();
    }
  };

  const fetchAuthorizedUsersData = async () => {
    console.log(
      `[useUsersSummary][fetchAuthorizedUsersData] Before calling fetchAuthorizedUsersData...`,
      { organizationUnitId },
    );
    setAuthUsersLoading(true);
    try {
      const data: AuthorizedUsersResponse = await userService.getAuthUsers(
        organizationUnitId,
        scope,
      );

      console.log(
        `[useUsersSummary][fetchAuthorizedUsersData] After calling fetchAuthorizedUsersData`,
        { data },
      );
      setAuthUsersAPIResponse(data);
    } catch (error: unknown) {
      errorToast(error);
      console.error(
        `[useUsersSummary][fetchAuthorizedUsersData] Error:`,
        error,
      );
      setAuthUsersLoading(false);
    }
  };

  const filterAuthorizedUsers = () => {
    setAuthUsersSummary(() => {
      const filteredUsers = usersSummary?.data.filter((user) =>
        authUsersAPIResponse?.data.some((authUser) => authUser.id === user.id),
      );

      const authorizedUsers: GetUsersResponse[] = [
        {
          addedBy: {
            firstName: '',
            lastName: '',
            date: new Date(),
          },
          email: validAuthToken?.email,
          firstName: validAuthToken?.firstName,
          id: authUsersAPIResponse
            ? authUsersAPIResponse.data.filter(
                (authUser) => authUser.email === validAuthToken?.email,
              )[0]?.id
            : validAuthToken?.['cognito:username'],
          lastName: validAuthToken?.lastName,
          mobileNumber: 123,
          organization: { id: '', name: '' },
          status: UserStatus.ACTIVE,

          type: validAuthToken?.type,
        } as GetUsersResponse,
        ...(filteredUsers || []),
      ];

      console.log(
        `[useUsersSummary][filterAuthorizedUsers] Filtered Authorized Users: `,
        { authorizedUsers },
      );

      return authorizedUsers;
    });

    setAuthUsersLoading(false);
  };

  return { authUsersLoading, authUsersSummary, refreshData };
};
